import {
    formatPhone,
    normalizeContactPhoneForStorage,
    phoneValidationError
} from '@/utils/phone';

const CONTACT_TYPES = ['phone', 'email', 'telegram'];

function contactError(message, code, index) {
    const error = new Error(message);
    error.code = code;
    error.contactIndex = index;
    return error;
}

export function normalizeEmployeeContactEmail(value) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
        throw new Error('Введите корректный адрес электронной почты.');
    }
    return normalized;
}

export function normalizeEmployeeContactTelegram(value) {
    const raw = String(value || '').trim();
    const match = raw.match(
        /^(?:https?:\/\/)?(?:www\.)?(?:t\.me|telegram\.me)\/@?([^/?#]+)\/?$/i
    );
    const username = (match ? match[1] : raw).replace(/^@+/, '');
    if (!/^[a-z0-9_]{5,64}$/i.test(username)) {
        throw new Error(
            'Введите username Telegram в формате @username или ссылку t.me/username.'
        );
    }
    return username.toLowerCase();
}

export function prepareEmployeeContact(contact, index = 0) {
    const row = contact && typeof contact === 'object' ? contact : {};
    const label = String(row.label || '').trim();
    const rawValue = String(row.value || '').trim();
    if (!label && !rawValue) return null;
    if (!rawValue) {
        throw contactError(
            `Укажите значение контакта №${index + 1}.`,
            'EMPLOYEE_CONTACT_VALUE_REQUIRED',
            index
        );
    }
    const contactType = String(row.contact_type || row.type || '').toLowerCase();
    if (CONTACT_TYPES.indexOf(contactType) === -1) {
        throw contactError(
            `Выберите тип контакта №${index + 1}.`,
            'EMPLOYEE_CONTACT_TYPE_REQUIRED',
            index
        );
    }
    let normalizedValue;
    let value;
    try {
        if (contactType === 'phone') {
            const validationMessage = phoneValidationError(rawValue, {mode: 'contact'});
            if (validationMessage) throw new Error(validationMessage);
            normalizedValue = normalizeContactPhoneForStorage(rawValue);
            value = formatPhone(normalizedValue.indexOf('ext:') === 0
                ? normalizedValue : `+${normalizedValue}`);
        } else if (contactType === 'email') {
            normalizedValue = normalizeEmployeeContactEmail(rawValue);
            value = normalizedValue;
        } else {
            normalizedValue = normalizeEmployeeContactTelegram(rawValue);
            value = `@${normalizedValue}`;
        }
    } catch (error) {
        throw contactError(
            error.message || `Проверьте контакт №${index + 1}.`,
            'EMPLOYEE_CONTACT_INVALID',
            index
        );
    }
    return {
        id: row.id || null,
        contact_type: contactType,
        label: label || null,
        value,
        normalized_value: normalizedValue,
        sort_order: index,
        source: row.source || 'manual'
    };
}

export function employeeContactIdentity(contact) {
    const prepared = prepareEmployeeContact(contact);
    return prepared
        ? `${prepared.contact_type}:${prepared.normalized_value}`
        : null;
}

export function prepareEmployeeContacts(methods, options = {}) {
    const prepared = [];
    const seen = new Set();
    const primary = new Set((options.primaryContacts || []).map(function (contact) {
        try {
            return employeeContactIdentity(contact);
        } catch (error) {
            return null;
        }
    }).filter(Boolean));
    (Array.isArray(methods) ? methods : []).forEach(function (contact, index) {
        const value = prepareEmployeeContact(contact, index);
        if (!value) return;
        const identity = `${value.contact_type}:${value.normalized_value}`;
        if (primary.has(identity)) {
            throw contactError(
                'Этот контакт уже используется как основной в учётной записи.',
                'EMPLOYEE_CONTACT_MATCHES_PRIMARY_ACCOUNT',
                index
            );
        }
        if (seen.has(identity)) {
            throw contactError(
                'Этот контакт уже добавлен.',
                'EMPLOYEE_CONTACT_DUPLICATE',
                index
            );
        }
        seen.add(identity);
        prepared.push(Object.assign({}, value, {sort_order: prepared.length}));
    });
    return prepared;
}

export function employeeContactsValidationError(methods, options = {}) {
    try {
        prepareEmployeeContacts(methods, options);
        return null;
    } catch (error) {
        return error.message || 'Проверьте контактные данные.';
    }
}

export default {
    employeeContactIdentity,
    employeeContactsValidationError,
    normalizeEmployeeContactEmail,
    normalizeEmployeeContactTelegram,
    prepareEmployeeContact,
    prepareEmployeeContacts
};
